export interface UserNameInput {
  first_name?: string | null;
  last_name?: string | null;
  full_name?: string | null;
  username?: string | null;
  user_id?: string | number | null;
}

export interface UserDisplayName {
  /** Full display name: "John Doe @johndoe", or "@johndoe", or "ID: 123", etc. */
  displayName: string;
  /** Name part only (no username): "John Doe", or "John", or "" */
  shortName: string;
  /** Formatted username with @, or null */
  usernameFormatted: string | null;
  /** First letter for avatar fallback ("J", "@", "?") */
  fallbackLetter: string;
}

/**
 * Adds "@" prefix to username if missing.
 */
export function formatUsername(raw: string): string {
  return raw.startsWith("@") ? raw : `@${raw}`;
}

/**
 * Unified user name formatter with greedy fallback:
 * first_name+last_name -> first_name -> full_name -> @username -> ID:{user_id} -> "Unknown"
 */
export function formatUserName(user: UserNameInput | null | undefined): UserDisplayName {
  if (!user) {
    return { displayName: "Unknown", shortName: "", usernameFormatted: null, fallbackLetter: "?" };
  }

  const { first_name, last_name, full_name, username, user_id } = user;

  // Build short name (name part without username)
  let shortName = "";
  if (first_name && last_name) {
    shortName = `${first_name} ${last_name}`;
  } else if (first_name) {
    shortName = first_name;
  } else if (full_name) {
    shortName = full_name;
  }

  // Format username
  const usernameFormatted = username ? formatUsername(username) : null;

  // Build full display name
  let displayName: string;
  if (shortName && usernameFormatted) {
    displayName = `${shortName} ${usernameFormatted}`;
  } else if (shortName) {
    displayName = shortName;
  } else if (usernameFormatted) {
    displayName = usernameFormatted;
  } else if (user_id != null && String(user_id)) {
    displayName = `ID: ${user_id}`;
  } else {
    displayName = "Unknown";
  }

  // Fallback letter for avatar
  const fallbackLetter = first_name?.[0] || full_name?.[0] || username?.[0] || "?";

  return { displayName, shortName, usernameFormatted, fallbackLetter };
}
